import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { NavigationItem } from '@/data/types';

interface NavigationProps {
  items: NavigationItem[];
  isMobile?: boolean;
  className?: string;
  onItemClick?: () => void;
}

export function Navigation({ items, isMobile = false, className = '', onItemClick }: NavigationProps) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const handleMouseEnter = (itemId: string) => {
    if (!isMobile) {
      setOpenDropdown(itemId);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setOpenDropdown(null);
    }
  };

  const handleToggleDropdown = (itemId: string) => {
    if (isMobile) {
      setOpenDropdown(openDropdown === itemId ? null : itemId);
    }
  };

  const handleLinkClick = () => {
    if (onItemClick) {
      onItemClick();
    }
  };

  if (isMobile) {
    return (
      <nav className={`mobile-navigation ${className}`}>
        <ul className="flex flex-col">
          {items.map((item) => (
            <li key={item.id || item.title} className="border-b border-slate-600">
              <div className="flex items-center justify-between">
                <Link
                  to={item.href}
                  className="block px-4 py-3 text-white hover:bg-slate-600 transition-colors flex-1"
                  onClick={handleLinkClick}
                >
                  {item.title}
                </Link>
                {item.children && (
                  <button
                    className="px-4 py-3 text-white hover:bg-slate-600 transition-colors"
                    onClick={() => handleToggleDropdown(item.id || item.title)}
                    aria-label={`Toggle ${item.title} submenu`}
                  >
                    <svg
                      className={`w-4 h-4 transform transition-transform ${
                        openDropdown === (item.id || item.title) ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>
                )}
              </div>
              
              {item.children && openDropdown === (item.id || item.title) && (
                <ul className="bg-slate-800">
                  {item.children.map((child, childIndex) => (
                    <li key={childIndex}>
                      <Link
                        to={child.href}
                        className="block px-8 py-2 text-white hover:bg-slate-700 transition-colors text-sm"
                        onClick={handleLinkClick}
                      >
                        {child.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </nav>
    );
  }

  return (
    <nav className={`desktop-navigation ${className}`}>
      <ul className="flex space-x-8">
        {items.map((item) => (
          <li
            key={item.id || item.title}
            className="relative group"
            onMouseEnter={() => handleMouseEnter(item.id || item.title)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to={item.href}
              className="block py-4 px-2 transition-colors text-sm font-medium uppercase tracking-wide"
              style={{ 
                color: '#e6e6e6',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffe474';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#e6e6e6';
              }}
              onClick={handleLinkClick}
            >
              {item.title}
            </Link>
            
            {item.children && (
              <ul
                className={`absolute top-full left-0 bg-white shadow-lg rounded-md min-w-48 z-50 transition-opacity duration-200 ${
                  openDropdown === (item.id || item.title)
                    ? 'opacity-100 visible'
                    : 'opacity-0 invisible'
                }`}
              >
                {item.children.map((child, childIndex) => (
                  <li key={childIndex}>
                    <Link
                      to={child.href}
                      className="block px-4 py-3 text-slate-700 hover:bg-slate-100 transition-colors text-sm border-b last:border-b-0 border-slate-200"
                      onClick={handleLinkClick}
                    >
                      {child.title}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}